import React, { Component } from 'react';
import { string, bool, func, array, object } from 'prop-types';
import { compose } from 'redux';
import { FormattedMessage, injectIntl, intlShape, FormattedDate } from '../../util/reactIntl';
import { Form as FinalForm } from 'react-final-form';
import classNames from 'classnames';
import isEqual from 'lodash/isEqual';
import {
  FieldTextInput,
  Form,
  FieldCurrencyInput,
  PrimaryButton,
  SecondaryButton,
  FieldDateInput,
  InfoIcon,
} from '../../components';
import { propTypes } from '../../util/types';
import { formatMoney } from '../../util/currency';
import { HiLightBulb } from 'react-icons/hi';
import { FiPaperclip } from 'react-icons/fi';
import { hasBlockingAttachments } from '../../ducks/messageAttachments.duck';
import OfferAttachmentsPicker from './OfferAttachmentsPicker';

import css from './CustomOfferForm.css';
import config from '../../config';
import { composeValidators, required, maxLength } from '../../util/validators';
import { types as sdkTypes } from '../../util/sdkLoader';
import { INVOICE_PAYMENT_TERM_DAYS } from '../../util/invoicing';
import { previewTotals, legacyPreviewResolution, rateLabelFor } from '../../util/vat';

const { Money } = sdkTypes;

const BLUR_TIMEOUT_MS = 100;
const TITLE_MAX_LENGTH = 80;

class CustomOfferFormComponent extends Component {
  constructor(props) {
    super(props);
    this.state = {
      currentStep: 1, // 1 = form input, 2 = confirmation
    };
    this.handleFocus = this.handleFocus.bind(this);
    this.handleBlur = this.handleBlur.bind(this);
    this.handleNextStep = this.handleNextStep.bind(this);
    this.handleBackStep = this.handleBackStep.bind(this);
    this.calculatePrices = this.calculatePrices.bind(this);
    this.blurTimeoutId = null;
  }

  componentDidUpdate(prevProps) {
    // The modal keeps this form mounted while closed; reopening starts from
    // the edit step again, keeping whatever the user had typed.
    if (this.props.isOpen && !prevProps.isOpen && this.state.currentStep !== 1) {
      this.setState({ currentStep: 1 });
    }
  }

  handleNextStep() {
    this.setState({ currentStep: 2 });
  }

  handleBackStep() {
    this.setState({ currentStep: 1 });
  }

  // The provider types the customer's price (VAT 0); Freedomly's service fee
  // is carved out of it and billed to the customer, so the freelancer's own
  // price is derived (customer service fee spec, decision 1). Nothing
  // VAT-inclusive is derived here: the VAT depends on the customer's billing
  // details and payment method, which are known only once they accept.
  calculatePrices(customOfferAmount, resolution) {
    const customerPrice = customOfferAmount?.amount || 0;
    if (!(customerPrice > 0)) {
      return { freelancerShare: null };
    }
    const t = previewTotals({ path: resolution.path, resolution, customerPrice });
    return { freelancerShare: new Money(t.providerNet, 'EUR') };
  }

  handleFocus() {
    this.props.onFocus();
    window.clearTimeout(this.blurTimeoutId);
  }

  handleBlur() {
    // We only trigger a blur if another focus event doesn't come
    // within a timeout. This enables keeping the focus synced when
    // focus is switched between the message area and the submit
    // button.
    this.blurTimeoutId = window.setTimeout(() => {
      this.props.onBlur();
    }, BLUR_TIMEOUT_MS);
  }

  render() {
    const { currentStep } = this.state;

    return (
      <FinalForm
        // Callers build initialValues inline (fresh Money / Date instances each
        // render); the default shallow compare would then re-initialize and wipe
        // the user's edits whenever the parent re-renders, e.g. on an upload.
        initialValuesEqual={isEqual}
        {...this.props}
        render={formRenderProps => {
          const {
            rootClassName,
            className,
            isProvider,
            customOfferAmount,
            customOfferPlusVATAmount,
            customOfferVATAmount,
            customOfferDescription,
            customOfferStartDate,
            customOfferDeliveryDate,
            handleSubmit,
            inProgress,
            customOfferError,
            invalid,
            form,
            formId,
            intl,
            values,
            isProOrInvoiceCustomer,
            isModal,
            attachmentsEnabled,
            stagedAttachments,
            onUploadAttachment,
            onRemoveAttachment,
          } = formRenderProps;

          const classes = classNames(rootClassName || css.root, className);
          const attachments = stagedAttachments || [];
          // The block needs both handlers and a process version that stores the files.
          const showAttachments = !!(
            attachmentsEnabled &&
            onUploadAttachment &&
            onRemoveAttachment
          );
          // In-flight uploads have no file id yet and failed ones must be removed first.
          const attachmentsBlocking = showAttachments && hasBlockingAttachments(attachments);
          const submitInProgress = inProgress;
          const submitDisabled = invalid || submitInProgress || attachmentsBlocking;

          const proProcessDescription = intl.formatMessage({
            id: 'CustomOfferForm.proProcessDescription',
          });

          const titleLabel = intl.formatMessage({ id: 'CustomOfferForm.titleLabel' });
          const titlePlaceholder = intl.formatMessage({ id: 'CustomOfferForm.titlePlaceholder' });
          const titleRequiredMessage = intl.formatMessage({ id: 'CustomOfferForm.titleRequired' });
          const titleTooLongMessage = intl.formatMessage(
            { id: 'CustomOfferForm.titleTooLong' },
            { maxLength: TITLE_MAX_LENGTH }
          );

          const descriptionLabel = intl.formatMessage({ id: 'CustomOfferForm.descriptionLabel' });
          const descriptionPlaceholderMessage = intl.formatMessage({
            id: 'CustomOfferForm.descriptionPlaceholder',
          });
          const descriptionRequiredMessage = intl.formatMessage({
            id: 'CustomOfferForm.descriptionRequired',
          });

          const amountLabel = intl.formatMessage({ id: 'CustomOfferForm.amountLabel' });
          const amountPlaceholder = intl.formatMessage({ id: 'CustomOfferForm.amountPlaceholder' });
          const amountRequiredMessage = intl.formatMessage({
            id: 'CustomOfferForm.amountRequired',
          });

          const deliveryDatePlaceHolder = intl.formatMessage({
            id: 'CustomOfferForm.deliveryDatePlaceholder',
          });

          const deliveryDateRequiredMessage = intl.formatMessage({
            id: 'CustomOfferForm.deliveryDateRequired',
          });

          const deliveryDateLabel = intl.formatMessage({
            id: 'ProjectBidForm.deliveryDateLabel',
          });

          const startDateLabel = intl.formatMessage({ id: 'CustomOfferForm.startDateLabel' });
          const startDatePlaceHolder = intl.formatMessage({
            id: 'CustomOfferForm.startDatePlaceholder',
          });
          const startDateRequiredMessage = intl.formatMessage({
            id: 'CustomOfferForm.startDateRequired',
          });
          const startDateAfterDeliveryMessage = intl.formatMessage({
            id: 'CustomOfferForm.startDateAfterDelivery',
          });

          const formatDayMonthYear = date =>
            intl.formatDate(date, { year: 'numeric', month: 'numeric', day: 'numeric' });

          // Field-level validators get (value, allValues), which composeValidators
          // drops -- so the "not after the delivery date" rule is composed by hand.
          // Final Form re-runs it whenever either date changes.
          const validateStartDate = (value, allValues) => {
            const missing = required(startDateRequiredMessage)(value);
            if (missing) {
              return missing;
            }
            const deliveryDate = allValues?.deliveryDate?.date;
            return deliveryDate && value.date > deliveryDate
              ? startDateAfterDeliveryMessage
              : undefined;
          };

          const freelancerShareLabel = intl.formatMessage({
            id: 'CustomOfferForm.freelancerShareLabel',
          });
          const freelancerSharePlaceholder = intl.formatMessage({
            id: 'CustomOfferForm.freelancerSharePlaceholder',
          });

          // Calculate pricing values using helper method
          const offerResolution = this.props.vatResolution || legacyPreviewResolution();
          const { freelancerShare } = this.calculatePrices(
            values.customOfferAmount,
            offerResolution
          );
          // Shown under the prices in both steps.
          const vatNote = (
            <p className={css.pricingNote}>
              <FormattedMessage id="CustomOfferForm.vatCalculatedOnAccept" />
            </p>
          );
          // The made-offer summary prints the offer's own amount, not the form's.
          const offerSupplies = offerResolution.supplies || {};
          const offerCustomerSupply = offerSupplies.core || offerSupplies.customerSale;
          const offerRateLabel = rateLabelFor(offerCustomerSupply, intl.locale);
          const showOfferVat = !!offerCustomerSupply && offerCustomerSupply.rate > 0;
          // The customer's accept view prints the work and the fee of the
          // offer's own amount.
          const offerTotals = previewTotals({
            path: offerResolution.path,
            resolution: offerResolution,
            customerPrice: customOfferAmount ? customOfferAmount.amount : 0,
          });

          const modalProMessage = (
            <FormattedMessage
              id="TransactionPanel.acceptProModalMessage"
              values={{ days: INVOICE_PAYMENT_TERM_DAYS }}
            />
          );

          // Check if this is the provider creating a new offer (not viewing existing one)
          const isCreatingNewOffer = isProvider && !customOfferAmount;

          return isProvider ? (
            <Form className={classes} onSubmit={values => handleSubmit(values, form)}>
              <div className={css.titleBlock}>
                <h2 className={css.heading}>
                  <FormattedMessage
                    id={
                      currentStep === 2
                        ? 'CustomOfferForm.previewAndSendHeading'
                        : 'CustomOfferForm.createOfferHeading'
                    }
                  />
                </h2>
                {currentStep === 1 && (
                  <InfoIcon
                    className={css.infoIcon}
                    data-tip={intl.formatMessage({ id: 'ProjectBidForm.headingTooltip' })}
                    data-border={true}
                    data-text-color={'black'}
                    data-background-color={'white'}
                    data-border-color={'darkgrey'}
                    data-class={css.tooltipText}
                    data-place={'bottom'}
                  />
                )}
              </div>

              {/* Step 1: Form Input */}
              {currentStep === 1 && (
                <div>
                  <div className={css.sectionContainer}>
                    <FieldTextInput
                      id={formId ? `${formId}.title` : 'title'}
                      name="title"
                      className={css.titleField}
                      type="text"
                      label={titleLabel}
                      placeholder={titlePlaceholder}
                      validate={composeValidators(
                        required(titleRequiredMessage),
                        maxLength(titleTooLongMessage, TITLE_MAX_LENGTH)
                      )}
                    />
                    <h3 className={css.sectionTitle}>
                      <FormattedMessage id="CustomOfferForm.contentSectionTitle" />
                    </h3>
                    <FieldTextInput
                      id="description"
                      name="description"
                      className={css.descriptionField}
                      type="textarea"
                      // label={descriptionLabel}
                      placeholder={descriptionPlaceholderMessage}
                      validate={composeValidators(required(descriptionRequiredMessage))}
                      showSpacer={false}
                    />
                    {showAttachments ? (
                      <OfferAttachmentsPicker
                        stagedAttachments={attachments}
                        onUploadAttachment={onUploadAttachment}
                        onRemoveAttachment={onRemoveAttachment}
                      />
                    ) : null}
                  </div>

                  <div className={css.sectionContainer}>
                    <h3 className={css.sectionTitle}>
                      <FormattedMessage id="CustomOfferForm.pricingSectionTitle" />
                    </h3>

                    <div className={css.priceRow}>
                      <div className={css.priceRowItem}>
                        <FieldCurrencyInput
                          className={`${css.field} ${css.currencyField}`}
                          inputrootclass={css.field}
                          id={formId ? `${formId}.customOfferAmount` : 'customOfferAmount'}
                          name="customOfferAmount"
                          currencyConfig={config.currencyConfig}
                          label={amountLabel}
                          placeholder={amountPlaceholder}
                          validate={required(amountRequiredMessage)}
                          onFocus={this.handleFocus}
                          onBlur={this.handleBlur}
                        />
                      </div>
                      <div className={css.priceRowItemRight}>
                        <label className={css.calculatedInputLabel}>{freelancerShareLabel}</label>
                        <div className={css.calculatedInput}>
                          {freelancerShare ? (
                            <span className={css.priceValue}>
                              {formatMoney(intl, freelancerShare)}
                            </span>
                          ) : (
                            <span className={css.pricePlaceholder}>
                              {freelancerSharePlaceholder}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    {vatNote}
                  </div>

                  <div className={css.sectionContainer}>
                    <h3 className={css.sectionTitle}>
                      <FormattedMessage id="CustomOfferForm.deliverySectionTitle" />
                    </h3>
                    <div className={css.dateFields}>
                      <FieldDateInput
                        id="startDate"
                        name="startDate"
                        className={css.field}
                        label={startDateLabel}
                        placeholderText={startDatePlaceHolder}
                        validate={validateStartDate}
                      />
                      <FieldDateInput
                        id="deliveryDate"
                        name="deliveryDate"
                        className={css.field}
                        label={deliveryDateLabel}
                        placeholderText={deliveryDatePlaceHolder}
                        validate={required(deliveryDateRequiredMessage)}
                      />
                    </div>
                  </div>

                  <div className={css.offerSubmitContainer}>
                    <div>
                      {customOfferError ? (
                        <p className={css.error}>
                          <FormattedMessage id="CustomOfferForm.operationFailed" />
                        </p>
                      ) : null}
                    </div>
                    <PrimaryButton
                      rootClassName={css.submitButton}
                      type="button"
                      disabled={invalid || attachmentsBlocking}
                      onClick={this.handleNextStep}
                    >
                      <FormattedMessage id="CustomOfferForm.nextStep" />
                    </PrimaryButton>
                  </div>
                </div>
              )}

              {/* Step 2: Confirmation */}
              {currentStep === 2 && (
                <>
                  <div className={css.confirmationSection}>
                    <div className={css.sectionContainer}>
                      <div className={css.confirmationItem}>
                        <div className={css.confirmationTitle}>{values.title}</div>
                      </div>
                      <h3 className={css.sectionTitle}>
                        <FormattedMessage id="CustomOfferForm.contentSectionTitle" />
                      </h3>
                      <div className={css.confirmationItem}>
                        <div className={css.confirmationValue}>{values.description}</div>
                      </div>
                      {showAttachments && attachments.length > 0 ? (
                        <div className={css.confirmationItem}>
                          <h4 className={css.attachmentsTitle}>
                            <FormattedMessage id="CustomOfferForm.attachmentsTitle" />
                          </h4>
                          <ul className={css.previewAttachments}>
                            {attachments.map(attachment => (
                              <li key={attachment.tempId} className={css.previewAttachment}>
                                <FiPaperclip className={css.attachIcon} />
                                <span className={css.previewAttachmentName}>{attachment.name}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ) : null}
                    </div>

                    <div className={css.sectionContainer}>
                      <h3 className={css.sectionTitle}>
                        <FormattedMessage id="CustomOfferForm.pricingSectionTitle" />
                      </h3>

                      <div className={css.priceRow}>
                        <div className={css.priceRowItem}>
                          <label className={css.calculatedInputLabel}>{amountLabel}</label>
                          <div className={css.calculatedInput}>
                            {values.customOfferAmount ? (
                              <span className={css.priceValue}>
                                {formatMoney(intl, values.customOfferAmount)}
                              </span>
                            ) : (
                              <span className={css.pricePlaceholder}>-</span>
                            )}
                          </div>
                        </div>
                        <div className={css.priceRowItemRight}>
                          <label className={css.calculatedInputLabel}>{freelancerShareLabel}</label>
                          <div className={css.calculatedInput}>
                            {freelancerShare ? (
                              <span className={css.priceValue}>
                                {formatMoney(intl, freelancerShare)}
                              </span>
                            ) : (
                              <span className={css.pricePlaceholder}>-</span>
                            )}
                          </div>
                        </div>
                      </div>
                      {vatNote}
                    </div>

                    <div className={css.sectionContainer}>
                      <h3 className={css.sectionTitle}>
                        <FormattedMessage id="CustomOfferForm.deliverySectionTitle" />
                      </h3>
                      <div className={css.confirmationItem}>
                        <div className={css.confirmationLabel}>{startDateLabel}</div>
                        <div className={css.confirmationValue}>
                          {values.startDate?.date ? formatDayMonthYear(values.startDate.date) : '-'}
                        </div>
                      </div>
                      <div className={css.confirmationItem}>
                        <div className={css.confirmationLabel}>{deliveryDateLabel}</div>
                        <div className={css.confirmationValue}>
                          {values.deliveryDate?.date
                            ? formatDayMonthYear(values.deliveryDate.date)
                            : '-'}
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Payment flow explanation for provider */}
                  <div className={css.sectionContainer}>
                    <div className={css.paymentExplanation}>
                      <h3 className={css.paymentExplanationTitle}>
                        <HiLightBulb className={css.lightBulbIcon} />
                        <FormattedMessage id="CustomOfferForm.paymentFlowTitle" />
                      </h3>
                      <div className={css.paymentExplanationText}>
                        <FormattedMessage
                          id={
                            this.props.isLightEntrepreneur
                              ? 'CustomOfferForm.lightEntrepreneurPaymentExplanation'
                              : 'CustomOfferForm.freelancerPaymentExplanation'
                          }
                          values={{ lineBreak: <br />, b: chunks => <strong>{chunks}</strong> }}
                        />
                      </div>
                    </div>
                  </div>
                  <div className={css.offerSubmitContainer}>
                    {customOfferError ? (
                      <p className={css.error}>
                        <FormattedMessage id="CustomOfferForm.operationFailed" />
                      </p>
                    ) : null}
                    <div className={css.buttonGroup}>
                      <SecondaryButton
                        rootClassName={css.backButton}
                        type="button"
                        onClick={this.handleBackStep}
                      >
                        <FormattedMessage id="CustomOfferForm.backStep" />
                      </SecondaryButton>
                      <PrimaryButton
                        rootClassName={css.confirmButton}
                        inProgress={submitInProgress}
                        disabled={submitDisabled}
                        onFocus={this.handleFocus}
                        onBlur={this.handleBlur}
                      >
                        <FormattedMessage id="CustomOfferForm.makeAnOfferButton" />
                      </PrimaryButton>
                    </div>
                  </div>
                </>
              )}
            </Form>
          ) : (
            <Form className={classes} onSubmit={values => handleSubmit(values, form)}>
              <div className={css.customOfferDescription}>
                <FormattedMessage id="CustomOfferForm.customOfferDescription" />
              </div>

              {customOfferStartDate ? (
                <div className={css.deliveryContainer}>
                  <FormattedMessage id="CustomOfferForm.startDate" />
                  <FormattedDate value={customOfferStartDate} />
                </div>
              ) : null}
              <div className={css.deliveryContainer}>
                <FormattedMessage id="CustomOfferForm.deliveryDate" />
                <FormattedDate value={customOfferDeliveryDate} />
              </div>
              <div className={css.submitContainer}>
                <div className={css.customOfferPrice}>
                  <FormattedMessage
                    id="CustomOfferForm.priceLabel"
                    values={{ rateLabel: offerRateLabel }}
                  />
                  &nbsp;
                  {customOfferAmount ? formatMoney(intl, customOfferPlusVATAmount) : null}
                </div>
                {showOfferVat ? (
                  <div className={css.customOfferVAT}>
                    <FormattedMessage
                      id="CustomOfferForm.bidAmountVAT"
                      values={{ rateLabel: offerRateLabel }}
                    />
                    &nbsp;
                    {customOfferAmount ? formatMoney(intl, customOfferVATAmount) : null}
                  </div>
                ) : null}
                <div className={css.customOfferService}>
                  <FormattedMessage id="CustomOfferForm.serviceLabel" />
                  &nbsp;
                  <div className={css.customOfferServiceAmount}>
                    {customOfferAmount
                      ? formatMoney(intl, new Money(offerTotals.net, 'EUR'))
                      : null}
                  </div>
                </div>
                <div className={css.customOfferService}>
                  <FormattedMessage
                    id="CustomOfferForm.serviceFee"
                    values={{ percentage: offerTotals.feePercentage }}
                  />
                  &nbsp;
                  <div className={css.customOfferServiceAmount}>
                    {customOfferAmount
                      ? formatMoney(intl, new Money(offerTotals.fee, 'EUR'))
                      : null}
                  </div>
                </div>
                <div className={css.customOfferDescriptionContent}>{customOfferDescription}</div>
                <div className={css.errorContainer}>
                  {customOfferError ? (
                    <p className={css.error}>
                      <FormattedMessage id="CustomOfferForm.operationFailed" />
                    </p>
                  ) : null}
                </div>
                {isProOrInvoiceCustomer && isModal ? (
                  <div className={css.customOfferDescriptionPro}>{modalProMessage}</div>
                ) : null}
                <PrimaryButton
                  rootClassName={css.submitButtonAccept}
                  inProgress={submitInProgress}
                  disabled={submitDisabled}
                  onFocus={this.handleFocus}
                  onBlur={this.handleBlur}
                >
                  <FormattedMessage id="CustomOfferForm.acceptAnOffer" />
                </PrimaryButton>
              </div>
            </Form>
          );
        }}
      />
    );
  }
}

CustomOfferFormComponent.defaultProps = {
  rootClassName: null,
  className: null,
  inProgress: false,
  isOpen: false,
  attachmentsEnabled: false,
  stagedAttachments: [],
  onUploadAttachment: null,
  onRemoveAttachment: null,
  messagePlaceholder: null,
  onFocus: () => null,
  onBlur: () => null,
  sendMessageError: null,
  vatResolution: null,
};

CustomOfferFormComponent.propTypes = {
  rootClassName: string,
  className: string,
  inProgress: bool,
  // Offer attachments, rendered only when the transaction's process stores them.
  // Whether the surrounding modal is open; a reopen resets to the edit step.
  isOpen: bool,
  attachmentsEnabled: bool,
  stagedAttachments: array,
  onUploadAttachment: func,
  onRemoveAttachment: func,

  messagePlaceholder: string,
  onSubmit: func.isRequired,
  onFocus: func,
  onBlur: func,
  sendMessageError: propTypes.error,
  isLightEntrepreneur: bool,
  // The panel's VAT resolution (snapshot, ok preview or legacy) for the price preview.
  vatResolution: object,

  // from injectIntl
  intl: intlShape.isRequired,
};

const CustomOfferForm = compose(injectIntl)(CustomOfferFormComponent);

CustomOfferForm.displayName = 'CustomOfferForm';

export default CustomOfferForm;
